import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './Trading.module.css';
import compactStyles from './CompactTable.module.css';
import { TradingOrderBlotterRow } from '../../types/trading';
import { SymbolLogo } from '../common/SymbolLogo';
import { StrategyConfigView } from '../../features/order-ticket/strategy-backtest/components/StrategyConfigView';
import type { BacktestRequest } from '../../features/order-ticket/strategy-backtest/config';

interface OrderBlotterProps {
  orders: TradingOrderBlotterRow[];
  onCancelOrder: (orderId: string) => Promise<void> | void;
  onCancelStrategy: (strategyPositionId: string) => Promise<void> | void;
}

const DEFAULT_PAGE_SIZE = 20;

const isStrategyRow = (order: TradingOrderBlotterRow) => order.entry_kind === 'strategy';

const parseOptionSymbol = (
  raw: string,
): { underlying: string; expiration: string; optionSide: 'call' | 'put'; strike: number } | null => {
  const normalized = raw.trim().toUpperCase();
  const match = normalized.match(/^(?:O:)?([A-Z]+)(\d{6})([CP])(\d+)$/);
  if (!match) return null;

  const [, underlying, yymmdd, cpFlag, strikeCode] = match;
  const yy = yymmdd.slice(0, 2);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);

  return {
    underlying,
    expiration: `20${yy}-${mm}-${dd}`,
    optionSide: cpFlag === 'C' ? 'call' : 'put',
    strike: parseInt(strikeCode, 10) / 1000,
  };
};

const formatOptionStrike = (strike: number): string => strike.toFixed(3).replace(/\.?0+$/, '');

const formatBlotterSymbol = (symbol: string | null | undefined): string => {
  const trimmed = symbol?.trim();
  if (!trimmed) return '--';
  const parsed = parseOptionSymbol(trimmed);
  if (!parsed) return trimmed;
  const sideLabel = parsed.optionSide === 'call' ? 'Call' : 'Put';
  return `${parsed.underlying} ${parsed.expiration} ${sideLabel} ${formatOptionStrike(parsed.strike)}`;
};

const resolveBlotterLogoSymbol = (symbol: string | null | undefined): string => {
  const trimmed = symbol?.trim();
  if (!trimmed) return '';
  const parsed = parseOptionSymbol(trimmed);
  return parsed?.underlying ?? trimmed.toUpperCase();
};

/* ── Status badge helper ───────────────────────────────────────────── */

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'filled': return `${styles.statusBadge} ${styles.statusFilled}`;
    case 'pending': return `${styles.statusBadge} ${styles.statusPending}`;
    case 'cancelled': return `${styles.statusBadge} ${styles.statusCancelled}`;
    case 'scheduled': return `${styles.statusBadge} ${styles.statusScheduled}`;
    default: return styles.statusBadge;
  }
};

/* ── Sorting ───────────────────────────────────────────────────────── */

type SortField = 'symbol' | 'status' | 'time';
type SortDir = 'asc' | 'desc';

const getSortValue = (order: TradingOrderBlotterRow, field: SortField): string | number => {
  switch (field) {
    case 'symbol': return formatBlotterSymbol(order.symbol).toLowerCase();
    case 'status': return order.status;
    case 'time': return +new Date(order.created_at);
  }
};

interface StrategyGroupDisplay {
  strategyPositionId: string;
  parent: TradingOrderBlotterRow;
  children: TradingOrderBlotterRow[];
  totalChildCount: number;
}

type TopLevelBlotterItem =
  | { kind: 'group'; group: StrategyGroupDisplay }
  | { kind: 'row'; row: TradingOrderBlotterRow };

const rowMatchesFilter = (order: TradingOrderBlotterRow, query: string): boolean => {
  const sym = formatBlotterSymbol(order.symbol).toLowerCase();
  const status = order.status.toLowerCase();
  const stratName = (order.strategy_name || '').toLowerCase();
  return sym.includes(query) || status.includes(query) || stratName.includes(query);
};

const topLevelSortRow = (item: TopLevelBlotterItem): TradingOrderBlotterRow =>
  item.kind === 'group' ? item.group.parent : item.row;

const sortTopLevelItems = (
  items: TopLevelBlotterItem[],
  field: SortField | null,
  dir: SortDir,
): TopLevelBlotterItem[] => {
  const sorted = [...items];
  if (!field) {
    return sorted.sort((a, b) => {
      const aTime = +new Date(topLevelSortRow(a).created_at);
      const bTime = +new Date(topLevelSortRow(b).created_at);
      return bTime - aTime;
    });
  }

  sorted.sort((a, b) => {
    const aVal = getSortValue(topLevelSortRow(a), field);
    const bVal = getSortValue(topLevelSortRow(b), field);
    if (aVal < bVal) return dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return dir === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
};

const SortIndicator: React.FC<{ field: SortField; activeField: SortField | null; dir: SortDir }> = ({
  field, activeField, dir,
}) => {
  if (field !== activeField) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{dir === 'asc' ? '↑' : '↓'}</span>;
};

export const OrderBlotter: React.FC<OrderBlotterProps> = ({
  orders,
  onCancelOrder,
  onCancelStrategy,
}) => {
  const [cancelTarget, setCancelTarget] = useState<TradingOrderBlotterRow | null>(null);
  const [viewTarget, setViewTarget] = useState<TradingOrderBlotterRow | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [expandedStrategyRows, setExpandedStrategyRows] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(0);

  const viewConfig = useMemo<BacktestRequest | null>(() => {
    if (!viewTarget?.strategy_config || typeof viewTarget.strategy_config !== 'object') {
      return null;
    }
    return viewTarget.strategy_config as unknown as BacktestRequest;
  }, [viewTarget]);

  const query = filterText.trim().toLowerCase();
  const isFilterActive = query.length > 0;

  const groupedDisplay = useMemo(() => {
    const strategyParentsById = new Map<string, TradingOrderBlotterRow>();
    const strategyChildrenById = new Map<string, TradingOrderBlotterRow[]>();
    const standaloneRows: TradingOrderBlotterRow[] = [];

    for (const order of orders) {
      if (!isStrategyRow(order)) continue;
      const strategyPositionId = order.strategy_position_id?.trim();
      if (!strategyPositionId) continue;
      if (!strategyParentsById.has(strategyPositionId)) {
        strategyParentsById.set(strategyPositionId, order);
      }
    }

    for (const order of orders) {
      if (isStrategyRow(order)) continue;
      const strategyPositionId = order.strategy_position_id?.trim();
      if (!strategyPositionId) {
        standaloneRows.push(order);
        continue;
      }

      if (!strategyParentsById.has(strategyPositionId)) {
        continue;
      }

      const existing = strategyChildrenById.get(strategyPositionId) ?? [];
      existing.push(order);
      strategyChildrenById.set(strategyPositionId, existing);
    }

    const allGroups = Array.from(strategyParentsById.entries()).map(
      ([strategyPositionId, parent]): StrategyGroupDisplay => {
        const children = [...(strategyChildrenById.get(strategyPositionId) ?? [])].sort(
          (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
        );
        return {
          strategyPositionId,
          parent,
          children,
          totalChildCount: children.length,
        };
      },
    );

    const groups = !isFilterActive
      ? allGroups
      : allGroups
          .map((group) => {
            const parentMatches = rowMatchesFilter(group.parent, query);
            const matchingChildren = group.children.filter((child) => rowMatchesFilter(child, query));
            if (!parentMatches && matchingChildren.length === 0) {
              return null;
            }
            return {
              ...group,
              children: matchingChildren,
            };
          })
          .filter((group): group is StrategyGroupDisplay => Boolean(group));

    const filteredStandaloneRows = !isFilterActive
      ? standaloneRows
      : standaloneRows.filter((row) => rowMatchesFilter(row, query));

    const topLevelItems: TopLevelBlotterItem[] = [
      ...groups.map((group) => ({ kind: 'group' as const, group })),
      ...filteredStandaloneRows.map((row) => ({ kind: 'row' as const, row })),
    ];

    return {
      topLevelItems: sortTopLevelItems(topLevelItems, sortField, sortDir),
    };
  }, [orders, query, sortField, sortDir, isFilterActive]);

  const toggleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return field;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  const toggleStrategyExpanded = useCallback((strategyPositionId: string) => {
    setExpandedStrategyRows((prev) => ({
      ...prev,
      [strategyPositionId]: !prev[strategyPositionId],
    }));
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [query, sortField, sortDir]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(groupedDisplay.topLevelItems.length / DEFAULT_PAGE_SIZE)),
    [groupedDisplay.topLevelItems.length],
  );

  const clampedPage = useMemo(
    () => Math.min(currentPage, totalPages - 1),
    [currentPage, totalPages],
  );

  useEffect(() => {
    if (currentPage !== clampedPage) {
      setCurrentPage(clampedPage);
    }
  }, [currentPage, clampedPage]);

  const paginatedTopLevelItems = useMemo(() => {
    const start = clampedPage * DEFAULT_PAGE_SIZE;
    return groupedDisplay.topLevelItems.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [groupedDisplay.topLevelItems, clampedPage]);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((p) => Math.max(0, p - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
  }, [totalPages]);

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      setCancelling(true);
      if (isStrategyRow(cancelTarget)) {
        if (!cancelTarget.strategy_position_id) return;
        await onCancelStrategy(cancelTarget.strategy_position_id);
      } else {
        await onCancelOrder(cancelTarget.order_id);
      }
    } finally {
      setCancelling(false);
      setCancelTarget(null);
    }
  };

  const cancelTargetIsStrategy = cancelTarget ? isStrategyRow(cancelTarget) : false;
  const hasOrders = orders.length > 0;
  const hasFilteredResults = groupedDisplay.topLevelItems.length > 0;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>Order Blotter</header>

      {/* Filter input */}
      <div style={{ padding: '0 0 8px' }}>
        <input
          type="text"
          className={styles.input}
          placeholder="Filter by symbol or status…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          data-testid="order-blotter-filter"
          style={{ fontSize: '0.8rem', padding: '6px 10px' }}
        />
      </div>

      <div className={styles.tableWrap}>
        <table className={`${styles.table} ${compactStyles.compactTable}`}>
          <thead>
            <tr>
              <th
                className={compactStyles.sortableHeader}
                onClick={() => toggleSort('time')}
              >
                Time <SortIndicator field="time" activeField={sortField} dir={sortDir} />
              </th>
              <th>Strategy Name</th>
              <th
                className={compactStyles.sortableHeader}
                onClick={() => toggleSort('symbol')}
              >
                Symbol <SortIndicator field="symbol" activeField={sortField} dir={sortDir} />
              </th>
              <th>Side</th>
              <th>Qty</th>
              <th>Type</th>
              <th
                className={compactStyles.sortableHeader}
                onClick={() => toggleSort('status')}
              >
                Status <SortIndicator field="status" activeField={sortField} dir={sortDir} />
              </th>
              <th>Avg Fill</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {!hasOrders && (
              <tr>
                <td colSpan={9}>
                  <div className={styles.blotterEmptyState}>
                    <span className={styles.blotterEmptyIcon}>📋</span>
                    <span className={styles.blotterEmptyText}>No orders yet. Place an order to see it here.</span>
                  </div>
                </td>
              </tr>
            )}
            {hasOrders && !hasFilteredResults && (
              <tr>
                <td colSpan={9}>
                  <div className={styles.blotterEmptyState}>
                    <span className={styles.blotterEmptyIcon}>🔍</span>
                    <span className={styles.blotterEmptyText}>No orders match "{filterText}"</span>
                  </div>
                </td>
              </tr>
            )}
            {paginatedTopLevelItems.map((item) => {
              if (item.kind === 'row') {
                const order = item.row;
                const strategyRow = isStrategyRow(order);
                return (
                  <tr key={order.order_id}>
                    <td>{new Date(order.created_at).toLocaleTimeString()}</td>
                    <td>{strategyRow ? (order.strategy_name || '--') : '--'}</td>
                    <td>
                      {strategyRow ? '--' : (
                        <span className={compactStyles.symbolCellContent}>
                          <SymbolLogo symbol={resolveBlotterLogoSymbol(order.symbol)} size={16} />
                          <span className={compactStyles.symbolCellLabel}>{formatBlotterSymbol(order.symbol)}</span>
                        </span>
                      )}
                    </td>
                    <td>{strategyRow ? '--' : (order.side ? order.side.toUpperCase() : '--')}</td>
                    <td>{strategyRow ? '--' : (order.quantity ?? '--')}</td>
                    <td>{strategyRow ? '--' : (order.order_type ? order.order_type.toUpperCase() : '--')}</td>
                    <td>
                      <span className={statusBadgeClass(order.status)}>
                        {order.status}
                      </span>
                    </td>
                    <td>{strategyRow ? '--' : (order.avg_fill_price != null ? order.avg_fill_price.toFixed(2) : '--')}</td>
                    <td>
                      {strategyRow ? (
                        <div className={styles.rowActions}>
                          <button
                            className={styles.linkButton}
                            onClick={() => setViewTarget(order)}
                            data-testid="order-blotter-strategy-view"
                          >
                            View
                          </button>
                          {(order.status === 'scheduled' || order.status === 'pending') && order.strategy_position_id && (
                            <button
                              className={styles.linkButton}
                              onClick={() => setCancelTarget(order)}
                              data-testid="order-blotter-strategy-cancel"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      ) : order.status === 'pending' ? (
                        <button
                          className={styles.linkButton}
                          onClick={() => setCancelTarget(order)}
                          data-testid="order-blotter-manual-cancel"
                        >
                          Cancel
                        </button>
                      ) : (
                        '--'
                      )}
                    </td>
                  </tr>
                );
              }

              const { group } = item;
              const isExpanded = expandedStrategyRows[group.strategyPositionId] ?? false;
              const visibleChildCount = group.children.length;
              const childCountLabel = isFilterActive ? visibleChildCount : group.totalChildCount;

              return (
                <React.Fragment key={group.parent.order_id}>
                  <tr className={styles.blotterParentRow}>
                    <td>{new Date(group.parent.created_at).toLocaleTimeString()}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.blotterExpandButton}
                        onClick={() => toggleStrategyExpanded(group.strategyPositionId)}
                        data-testid="order-blotter-strategy-toggle"
                      >
                        <span className={styles.blotterExpander}>{isExpanded ? '▼' : '▶'}</span>
                        <span>{group.parent.strategy_name || '--'}</span>
                        <span className={styles.blotterChildCount}>({childCountLabel})</span>
                      </button>
                    </td>
                    <td>--</td>
                    <td>--</td>
                    <td>--</td>
                    <td>--</td>
                    <td>
                      <span className={statusBadgeClass(group.parent.status)}>
                        {group.parent.status}
                      </span>
                    </td>
                    <td>--</td>
                    <td>
                      <div className={styles.rowActions}>
                        <button
                          className={styles.linkButton}
                          onClick={() => setViewTarget(group.parent)}
                          data-testid="order-blotter-strategy-view"
                        >
                          View
                        </button>
                        {(group.parent.status === 'scheduled' || group.parent.status === 'pending') && group.parent.strategy_position_id && (
                          <button
                            className={styles.linkButton}
                            onClick={() => setCancelTarget(group.parent)}
                            data-testid="order-blotter-strategy-cancel"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && group.children.map((child) => (
                    <tr key={child.order_id} className={styles.blotterChildRow}>
                      <td>{new Date(child.created_at).toLocaleTimeString()}</td>
                      <td className={styles.blotterChildStrategyCell}>↳</td>
                      <td>
                        <span className={compactStyles.symbolCellContent}>
                          <SymbolLogo symbol={resolveBlotterLogoSymbol(child.symbol)} size={16} />
                          <span className={compactStyles.symbolCellLabel}>{formatBlotterSymbol(child.symbol)}</span>
                        </span>
                      </td>
                      <td>{child.side ? child.side.toUpperCase() : '--'}</td>
                      <td>{child.quantity ?? '--'}</td>
                      <td>{child.order_type ? child.order_type.toUpperCase() : '--'}</td>
                      <td>
                        <span className={statusBadgeClass(child.status)}>
                          {child.status}
                        </span>
                      </td>
                      <td>{child.avg_fill_price != null ? child.avg_fill_price.toFixed(2) : '--'}</td>
                      <td>--</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {groupedDisplay.topLevelItems.length > DEFAULT_PAGE_SIZE && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            padding: '0.4rem 0.75rem',
            fontSize: '0.8rem',
            borderTop: '1px solid var(--color-border, #333)',
            color: 'var(--color-text-secondary, #aaa)',
          }}
        >
          <button
            type="button"
            onClick={goToPreviousPage}
            disabled={clampedPage === 0}
            style={{
              background: 'none',
              border: 'none',
              color: clampedPage === 0
                ? 'var(--color-text-disabled, #555)'
                : 'var(--color-text-secondary, #aaa)',
              cursor: clampedPage === 0 ? 'default' : 'pointer',
              padding: '0.2rem 0.5rem',
            }}
          >
            ← Prev
          </button>
          <span>
            Page {clampedPage + 1} of {totalPages}
          </span>
          <button
            type="button"
            onClick={goToNextPage}
            disabled={clampedPage >= totalPages - 1}
            style={{
              background: 'none',
              border: 'none',
              color: clampedPage >= totalPages - 1
                ? 'var(--color-text-disabled, #555)'
                : 'var(--color-text-secondary, #aaa)',
              cursor: clampedPage >= totalPages - 1 ? 'default' : 'pointer',
              padding: '0.2rem 0.5rem',
            }}
          >
            Next →
          </button>
        </div>
      )}

      {cancelTarget && (
        <div className={styles.confirmBackdrop} onClick={() => !cancelling && setCancelTarget(null)}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmHeader}>
              <h3 className={styles.confirmTitle}>
                {cancelTargetIsStrategy ? 'Cancel Strategy' : 'Cancel Order'}
              </h3>
            </div>
            <div className={styles.confirmBody}>
              <p className={styles.confirmDescription}>
                {cancelTargetIsStrategy
                  ? 'Are you sure you want to cancel this strategy?'
                  : 'Are you sure you want to cancel this order?'}
              </p>
              {cancelTargetIsStrategy ? (
                <div className={styles.confirmRow}>
                  <span className={styles.confirmLabel}>Strategy</span>
                  <span className={styles.confirmValue}>{cancelTarget.strategy_name ?? '--'}</span>
                </div>
              ) : (
                <>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Symbol</span>
                    <span className={styles.confirmValue}>{formatBlotterSymbol(cancelTarget.symbol)}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Side</span>
                    <span className={styles.confirmValue}>{cancelTarget.side ? cancelTarget.side.toUpperCase() : '--'}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Quantity</span>
                    <span className={styles.confirmValue}>{cancelTarget.quantity ?? '--'}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Type</span>
                    <span className={styles.confirmValue}>{cancelTarget.order_type ? cancelTarget.order_type.toUpperCase() : '--'}</span>
                  </div>
                </>
              )}
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Status</span>
                <span className={styles.confirmValue}>{cancelTarget.status}</span>
              </div>
              {!cancelTargetIsStrategy && cancelTarget.avg_fill_price != null && (
                <div className={styles.confirmRow}>
                  <span className={styles.confirmLabel}>Avg Fill</span>
                  <span className={styles.confirmValue}>{cancelTarget.avg_fill_price.toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className={styles.confirmActions}>
              <button
                className={styles.confirmButtonNo}
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
              >
                {cancelTargetIsStrategy ? 'Keep Strategy' : 'Keep Order'}
              </button>
              <button
                className={styles.confirmButtonDanger}
                onClick={handleConfirmCancel}
                disabled={cancelling}
                data-testid="order-blotter-confirm-cancel"
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewTarget && (
        <div
            className={styles.confirmBackdrop}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setViewTarget(null);
              }
            }}
        >
          <div className={styles.confirmModal} role="dialog" aria-modal="true" data-testid="order-blotter-strategy-modal">
            <div className={styles.confirmHeader}>
              <h3 className={styles.confirmTitle}>{viewTarget.strategy_name || 'Strategy'} Configuration</h3>
            </div>
            <div className={styles.confirmBody}>
              <StrategyConfigView config={viewConfig} />
            </div>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmButtonYes}
                onClick={() => setViewTarget(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
